import { Button } from 'flowbite-react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useRef } from 'react';
import { isAxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';
import axiosClient from 'api/config/axios';
import { OrderStatus } from 'utils/enums';
import { useIsTabletAndLess } from 'hooks/media-query';
import { HorizontalDashedDelimiter } from 'components/DashedDelimiter';
import { useModals } from 'providers/Modals';
import { ParsedOrder } from 'api/order.parsers';
import useOrderCanLeaveReview from 'hooks/useOrderCheckLeaveReview';
import { AimeosProductType } from 'api/types';
import { useCancelTradeMutation } from 'api/mutations/trades/cancel';
import { useEscrowCancelTradeMutation } from 'api/mutations/escrow/cancel-trade';
import { memoryQueryClient } from 'providers/TanstackQuery';
import { getOrderBaseQueryKey } from 'api/queries/product/get-order';
import { useCompleteTradeMutation } from 'api/mutations/trades/complete';
import { useCompleteEscrowPaymentMutation } from 'api/mutations/escrow/complete-payment';
import { getEscrowByTradeQueryKey, useGetEscrowByTradeQuery } from 'api/queries/escrow/get-escrow-by-trade';
import { useDataValues } from 'providers/DataValues';
import { getActiveDispute, hasOnlyTerminalDisputes } from 'utils/escrow-helpers';
import { useChatModal } from 'providers/ChatModalProvider';
import { useInitiatePayment } from 'api/mutations/escrow/initiate-payment';
import {
  DEFAULT_PAYMENT_PROVIDERS,
  getPaymentProvidersQueryKey,
  useGetPaymentProvidersQuery,
} from 'api/queries/escrow/get-payment-providers';
import { KEYS, logEvent } from 'utils/analytics';
import { currencyFormatter } from 'utils/currency-formatter';
import { flattenProviderOptions } from 'utils/flatten-provider-options';

export default function Actions({ trade, order }: { order?: ParsedOrder; trade?: ParsedOrder }) {
  const navigate = useNavigate();
  const goBack = () => navigate(-1);
  const { mutateAsync: completeTrade } = useCompleteTradeMutation();
  const { mutateAsync: completeEscrowPayment } = useCompleteEscrowPaymentMutation();
  const { userId } = useDataValues();

  const { data: escrow } = useGetEscrowByTradeQuery(+(trade?.id || '0'), {
    enabled: !!trade?.id,
  });

  const isBuyer = escrow?.buyerId !== undefined && +userId === escrow.buyerId;
  const showCompleteButton = !escrow || isBuyer;

  const isTabletAndLess = useIsTabletAndLess();
  const buttonSize = isTabletAndLess ? 'sm' : 'lg';

  const orderStatus = trade?.tradeStatus || OrderStatus.Canceled;

  const { open: openModal } = useModals();
  const { openChat } = useChatModal();

  const showRateButton = useOrderCanLeaveReview(AimeosProductType.Trade, order, trade);

  const leaveReview = () => {
    if (trade && order) {
      openModal('leave-review', {
        orderId: order.id || '',
        orderOwnerId: order.owner || '',
        orderCode: order.code || '',
        orderType: order.orderType,
      });
    }
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const { mutateAsync: initiatePayment } = useInitiatePayment();
  const { data: providerMap } = useGetPaymentProvidersQuery();

  // Forward reference to openPayFlow so handlePayClick's anti-loop branches
  // can re-open the picker after invalidating the providers cache. openPayFlow
  // is defined further down (it depends on handlePayClick), so the ref breaks
  // the cycle.
  const openPayFlowRef = useRef<
    ((paymentOrderId: string, currency: string, options?: { retryAttempted?: boolean }) => void) | null
  >(null);

  /**
   * Initiates payment for a given payment order with the chosen driver and
   * optional Xendit sub-method.
   *
   * The function uses two sequential try/catch blocks so that an error from
   * the pay endpoint can be told apart from an error during the checkout
   * redirect hop. Each catch is an inline closure with access to the local
   * `currency`, `paymentOrderId`, `driver`, `paymentMethod`, and the
   * `retryAttempted` anti-loop flag.
   *
   * Anti-loop rule: a 422 from the pay endpoint OR a stale-method 409 from
   * the checkout hop triggers ONE automatic re-prompt (cache invalidation +
   * re-open picker). A second occurrence falls through to a generic snackbar
   * with no further re-prompt, preventing modal loops on persistently
   * misconfigured backends.
   *
   * Redirects the browser to the checkout URL returned by the payment provider.
   */
  const handlePayClick = useCallback(
    async (
      paymentOrderId: string,
      currency: string,
      driver?: string,
      paymentMethod?: string,
      retryAttempted: boolean = false,
    ) => {
      const numericId = Number(paymentOrderId);
      if (!Number.isFinite(numericId) || numericId <= 0) {
        enqueueSnackbar('Invalid payment order. Please reload the page.', { variant: 'error' });
        return;
      }

      /** Invalidates the providers cache and re-opens the picker — used by both anti-loop branches. */
      const reopenPickerWithFreshProviders = () => {
        void memoryQueryClient.invalidateQueries({ queryKey: getPaymentProvidersQueryKey() });
        openPayFlowRef.current?.(paymentOrderId, currency, { retryAttempted: true });
      };

      // first try/catch — pay endpoint
      let result: Awaited<ReturnType<typeof initiatePayment>>;
      try {
        result = await initiatePayment({ paymentOrderId: numericId, driver, paymentMethod });
      } catch (err: unknown) {
        if (isAxiosError(err) && err.response?.status === 409) {
          // genuine provider/method-unavailable — display message, no retry
          const message = (err.response.data as { message?: string })?.message || 'Payment method unavailable.';
          enqueueSnackbar(message, { variant: 'error' });
        } else if (isAxiosError(err) && err.response?.status === 422) {
          // missing or unrecognised payment_method — anti-loop guarded auto-retry
          const message =
            (err.response.data as { message?: string })?.message || 'Selected payment method is unavailable.';
          enqueueSnackbar(message, { variant: 'error' });
          if (!retryAttempted) {
            reopenPickerWithFreshProviders();
          } else {
            enqueueSnackbar('Payment is currently unavailable. Please try again later.', { variant: 'error' });
          }
        } else {
          enqueueSnackbar('Failed to open payment page. Please try again.', { variant: 'error' });
        }
        return;
      }

      // second try/catch — checkout-redirect hop
      try {
        const checkoutResponse = await axiosClient.query.get(result.checkout_url, {
          headers: { Accept: 'application/json' },
        });
        const redirectUrl = checkoutResponse.data?.redirect_url;
        // payment gateways always serve over HTTPS — reject plain HTTP redirects to avoid
        // leaking checkout tokens / sensitive query parameters in transit
        if (typeof redirectUrl === 'string' && redirectUrl.startsWith('https://')) {
          window.location.href = redirectUrl;
        } else {
          enqueueSnackbar('Invalid payment URL received. Please try again.', { variant: 'error' });
        }
      } catch (err: unknown) {
        if (isAxiosError(err) && err.response?.status === 409) {
          // stale payment_method on checkout-retry — anti-loop guarded auto-retry
          const message =
            (err.response.data as { message?: string })?.message || 'Payment method is no longer available.';
          enqueueSnackbar(message, { variant: 'error' });
          if (!retryAttempted) {
            reopenPickerWithFreshProviders();
          } else {
            enqueueSnackbar('Payment is currently unavailable. Please try again later.', { variant: 'error' });
          }
        } else if (isAxiosError(err) && err.response?.status === 404) {
          enqueueSnackbar('Payment checkout URL not available, please retry.', { variant: 'error' });
        } else {
          enqueueSnackbar('Failed to open payment page. Please try again.', { variant: 'error' });
        }
      }
    },
    [initiatePayment],
  );

  /**
   * Opens the combined payment-method picker for a given payment order.
   *
   * Flattens the provider/method matrix into a single list of payment
   * options (e.g. "PayPal", "GCash", "QRPh", "PayMaya") so the buyer
   * picks what they actually recognise without first choosing a PSP.
   * Each option carries both the backend `driver` and the optional
   * Xendit sub-method code.
   *
   * Decision tree:
   * - 0 options for the currency → error snackbar
   * - 1+ options → open the combined picker
   *
   * The `flowOptions.retryAttempted` flag is forwarded to `handlePayClick`
   * so the Step 7 anti-loop logic survives across the picker hop. The
   * anti-loop branches inside `handlePayClick` are the only callers that
   * ever pass `{ retryAttempted: true }`.
   */
  const openPayFlow = useCallback(
    (paymentOrderId: string, currency: string, flowOptions?: { retryAttempted?: boolean }) => {
      const retryAttempted = flowOptions?.retryAttempted ?? false;

      // use defaults while the query is still loading to avoid calling initiatePayment with undefined driver
      const map = providerMap ?? DEFAULT_PAYMENT_PROVIDERS;
      const providers = map[currency] ?? [];

      // flatten provider/method matrix into a single list of payment options
      const paymentOptions = flattenProviderOptions(providers);

      // no options available — show error instead of sending undefined driver
      if (paymentOptions.length === 0) {
        enqueueSnackbar(`No payment providers are available for ${currency}. Please contact support.`, {
          variant: 'error',
        });
        return;
      }

      // always show the picker so the user confirms which method they are paying with
      openModal('method-selection', {
        currency,
        options: paymentOptions,
        onOptionSelected: (driver: string, paymentMethod?: string) => {
          void handlePayClick(paymentOrderId, currency, driver, paymentMethod, retryAttempted);
        },
      });
    },
    [handlePayClick, openModal, providerMap],
  );

  // Wire the forward reference so handlePayClick's anti-loop branches can call back into openPayFlow.
  openPayFlowRef.current = openPayFlow;

  const showReviewModal = showRateButton && searchParams.get('action') === 'leave-review';
  const showChatModal =
    (showRateButton || trade?.tradeStatus === OrderStatus.Traded) && searchParams.get('action') === 'open-chat';

  useEffect(() => {
    if (showReviewModal) {
      leaveReview();
    } else if (showChatModal) {
      openChat(trade?.id);
    }
  }, [showReviewModal, showChatModal]);

  // Separate effect for ?action=pay: must wait for escrow to load before consuming the param.
  // Uses a ref guard instead of setSearchParams to avoid clobbering other param cleanups
  // (e.g., fromPayment in TradeDetailsPage). The param stays in the URL but handlePayClick
  // triggers a full-page redirect to Xendit, so it disappears naturally.
  const showPayAction = searchParams.get('action') === 'pay';
  const actionConsumed = useRef(false);
  useEffect(() => {
    if (!showPayAction || !escrow || actionConsumed.current) return;

    // guard: if escrow is closed, inform the user and clean the stale deep-link param
    if (escrow.closedAt) {
      actionConsumed.current = true;
      enqueueSnackbar('Payment is no longer required — this escrow has been closed.', { variant: 'info' });
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('action');
          return next;
        },
        { replace: true },
      );
      return;
    }

    const myPaymentOrder = escrow.paymentOrders?.find((po) => po.userId === +userId);

    // guard: if payment order is missing or terminal, consume the action and clean the param
    if (!myPaymentOrder || ['paid', 'canceled', 'refunded'].includes(myPaymentOrder.status)) {
      actionConsumed.current = true;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('action');
          return next;
        },
        { replace: true },
      );
      return;
    }

    actionConsumed.current = true;
    openPayFlow(myPaymentOrder.id, myPaymentOrder.currency);
  }, [showPayAction, escrow, userId, openPayFlow, setSearchParams]);

  // Show payment breakdown dialog when arriving from trade acceptance.
  // Uses a ref guard to fire once, then strips the ?accepted param from the URL.
  const showAccepted = searchParams.get('accepted') === 'true';
  const acceptedConsumed = useRef(false);
  useEffect(() => {
    if (!showAccepted || !escrow || acceptedConsumed.current) return;
    acceptedConsumed.current = true;

    // escrow already closed — silently strip the stale param without showing the breakdown modal
    if (escrow.closedAt) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('accepted');
          return next;
        },
        { replace: true },
      );
      return;
    }

    const myPaymentOrder = escrow.paymentOrders?.find((po) => po.userId === +userId);
    if (!myPaymentOrder || ['paid', 'canceled', 'refunded'].includes(myPaymentOrder.status)) {
      // payment already handled — clean param and bail
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('accepted');
          return next;
        },
        { replace: true },
      );
      return;
    }

    // compute breakdown (mirrors EscrowPanel logic)
    const isBuyerRole = +userId === escrow.buyerId;
    const purchasePrice = +escrow.price * +escrow.quantity;
    const depositAmount = +(isBuyerRole ? escrow.buyerEscrowDepositAmount : escrow.sellerEscrowDepositAmount);
    const feeAmount = +(isBuyerRole ? escrow.exchangeFeeBuyer : escrow.exchangeFeeSeller);
    const remainder = purchasePrice - depositAmount;

    // format amounts using the trade currency
    const formatAmount = currencyFormatter(myPaymentOrder.currency).format;

    /** Payment breakdown JSX passed as descriptionElement to the cancel-confirm modal. */
    const breakdownElement = (
      <div className="w-full flex flex-col gap-2 text-sm text-brand-black">
        <div className="flex justify-between">
          <span>Escrow deposit</span>
          <span className="font-medium">{formatAmount(depositAmount)}</span>
        </div>
        {isBuyerRole && (
          <div className="flex justify-between">
            <span>Remainder</span>
            <span className="font-medium">{formatAmount(remainder)}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span>Exchange fee</span>
          <span className="font-medium">{formatAmount(feeAmount)}</span>
        </div>
        <div className="border-t border-gray-300 my-1" />
        <div className="flex justify-between font-bold">
          <span>Total</span>
          <span>{formatAmount(+myPaymentOrder.amount)}</span>
        </div>
      </div>
    );

    openModal('cancel-confirm', {
      titleElement: (
        <>
          <b>Payment Required</b>
        </>
      ),
      descriptionElement: breakdownElement,
      image: 'manAndComputer',
      cancel: { label: 'Later' },
      confirm: {
        label: 'Pay Now',
        onClick: (closeModal) => {
          closeModal();
          openPayFlow(myPaymentOrder.id, myPaymentOrder.currency);
        },
      },
    });

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('accepted');
        return next;
      },
      { replace: true },
    );
  }, [showAccepted, escrow, userId, openModal, setSearchParams, openPayFlow]);

  const { mutateAsync: cancelTradeMutation } = useCancelTradeMutation();
  const { mutateAsync: escrowCancelTradeMutation } = useEscrowCancelTradeMutation();
  const cancelTrade = () => {
    const hasEscrow = !!escrow?.activatedAt;
    openModal('prompt-confirm', {
      title: (
        <>
          {'Are you sure you want to'}
          <br />
          <b>Cancel Trade?</b>
          {hasEscrow && (
            <p className="text-sm font-normal text-gray-500 mt-2">
              Any escrow deposits will be refunded automatically.
            </p>
          )}
        </>
      ),
      confirmLabel: 'Cancel Trade',
      cancelLabel: 'Back',
      confirm: {
        asyncOnClick: async (reason, closeModal) => {
          if (orderStatus === OrderStatus.PendingFunding) {
            await escrowCancelTradeMutation({ tradeId: +(trade?.id || '0'), reason });
          } else {
            await cancelTradeMutation({ cancel_reason: reason, trade_id: trade?.id || '' });
          }
          closeModal();
          memoryQueryClient.refetchQueries({
            queryKey: getOrderBaseQueryKey,
          });
          leaveReview();
        },
      },
    });
  };

  const isEscrowCompletion = escrow?.activatedAt && +userId === escrow?.buyerId;

  const onCompleteTrade = () => {
    logEvent(KEYS.trade_complete_clicked, { tradeId: trade?.id, isEscrow: !!isEscrowCompletion });

    openModal('cancel-confirm', {
      cancel: {
        label: 'Back',
      },
      titleElement: (
        <>
          {'Are you sure you want to '}
          <b>Complete Trade?</b>
        </>
      ),
      image: 'manAndComputer',
      confirm: {
        label: 'Complete Trade',
        asyncOnClick: async (closeModal) => {
          if (escrow && !escrow.activatedAt && +userId === escrow.buyerId) {
            enqueueSnackbar('Payment must be completed before finishing the trade.', { variant: 'error' });
            closeModal();
            return;
          }
          try {
            if (isEscrowCompletion) {
              await completeEscrowPayment({ tradeId: +(trade?.id || '0') });
            } else {
              await completeTrade({ trade_id: trade?.id || '' });
            }
            closeModal();

            logEvent(KEYS.trade_complete_succeeded, { tradeId: trade?.id, isEscrow: !!isEscrowCompletion });

            memoryQueryClient.refetchQueries({
              queryKey: getOrderBaseQueryKey,
            });

            // escrow path: async payout copy; legacy path: immediate review prompt
            if (isEscrowCompletion) {
              enqueueSnackbar('Trade completed. Settlement is final and payouts are now processing.', {
                variant: 'success',
              });
              memoryQueryClient.invalidateQueries({
                queryKey: getEscrowByTradeQueryKey(+(trade?.id || '0')),
              });
            } else {
              leaveReview();
            }
          } catch (err: unknown) {
            if (isEscrowCompletion) {
              memoryQueryClient.invalidateQueries({
                queryKey: getEscrowByTradeQueryKey(+(trade?.id || '0')),
              });
            }

            // payout account required — show setup modal, retry on save
            const errorCode = isAxiosError(err) && (err.response?.data as { error_code?: string })?.error_code;

            if (errorCode === 'PAYOUT_ACCOUNT_REQUIRED') {
              closeModal();
              openModal('setup-payout', {
                currency: escrow?.currency,
                onSaved: () => onCompleteTrade(),
              });
              return;
            } else if (isAxiosError(err) && err.response?.status === 422) {
              enqueueSnackbar('Trade cannot be completed. Please check that all conditions are met.', {
                variant: 'error',
              });
            } else if (isAxiosError(err) && err.response?.status === 409) {
              enqueueSnackbar('Trade cannot be completed: escrow is not ready or a dispute is open.', {
                variant: 'error',
              });
            } else {
              throw err;
            }
            closeModal();
          }
        },
      },
    });
  };

  const reportProblem = () => {
    openModal('report-problem', { tradeId: trade?.id });
  };

  const RateButton = showRateButton ? (
    <Button
      size={buttonSize}
      pill
      color="dark"
      className="min-w-[92px] font-semibold border-0 whitespace-nowrap break-words"
      onClick={leaveReview}
    >
      Rate the other party
    </Button>
  ) : null;

  const CancelTradeButton = (
    <Button
      size={buttonSize}
      pill
      color="dark"
      className="min-w-[92px] font-semibold border-0 whitespace-nowrap break-words"
      onClick={cancelTrade}
    >
      Cancel Trade
    </Button>
  );

  const ReportAProblemButton = (
    <Button
      size={buttonSize}
      pill
      color="dark"
      className="min-w-[92px] font-semibold border-0 whitespace-nowrap break-words"
      onClick={reportProblem}
    >
      Report a problem
    </Button>
  );

  const OpenDisputeButton = escrow?.activatedAt ? (
    <Button
      size={buttonSize}
      pill
      color="dark"
      className="min-w-[92px] font-semibold border-0 whitespace-nowrap break-words"
      onClick={() => openModal('open-dispute', { tradeId: trade?.id || '' })}
    >
      Open Dispute
    </Button>
  ) : null;

  const ChatButton = (
    <Button
      size={buttonSize}
      pill
      color="dark"
      className="min-w-[92px] font-semibold border-0 whitespace-nowrap break-words"
      onClick={() => openChat(trade?.id || '')}
    >
      Chat
    </Button>
  );

  const GoBackButton = (
    <Button
      size={buttonSize}
      pill
      color="light"
      className="min-w-[92px] font-semibold border-0 bg-brand-purple-light"
      onClick={goBack}
    >
      Back
    </Button>
  );

  const CompleteTradeButton = (
    <Button
      size={buttonSize}
      pill
      color="dark"
      className="min-w-[92px] bg-brand-red-main font-semibold whitespace-nowrap break-words"
      onClick={onCompleteTrade}
    >
      Complete Trade
    </Button>
  );

  const activeDispute = escrow?.disputes ? getActiveDispute(escrow.disputes) : undefined;

  const ViewDisputeButton = activeDispute ? (
    <Button
      size={buttonSize}
      pill
      color="dark"
      className="min-w-[92px] font-semibold border-0 whitespace-nowrap break-words"
      onClick={() => navigate(`/disputes/${activeDispute.id}`)}
    >
      View Dispute
    </Button>
  ) : null;

  if (orderStatus === OrderStatus.Canceled) {
    return (
      <div className={'flex items-center gap-2 lg:gap-4 flex-wrap'}>
        {GoBackButton}
        {trade?.canBeReviewed && ChatButton}
        <div className={'ml-auto'} />
        {RateButton}
      </div>
    );
  }

  const myPaymentOrder = escrow?.paymentOrders.find((po) => po.userId === +userId);

  const showPayButton =
    myPaymentOrder !== undefined &&
    !escrow?.closedAt &&
    !['paid', 'canceled', 'refunded'].includes(myPaymentOrder.status);

  if (orderStatus === OrderStatus.PendingFunding) {
    return (
      <div className={'flex items-center gap-2 lg:gap-4 flex-wrap'}>
        {GoBackButton}
        {CancelTradeButton}
        {showPayButton && myPaymentOrder && (
          <>
            <div className={'ml-auto'} />
            <Button
              size={buttonSize}
              pill
              color="failure"
              className="min-w-[92px] bg-brand-red-main font-semibold whitespace-nowrap break-words"
              onClick={() => openPayFlow(myPaymentOrder.id, myPaymentOrder.currency)}
            >
              Pay
            </Button>
          </>
        )}
      </div>
    );
  }

  if (orderStatus === OrderStatus.DisputeOpen) {
    const showComplete = escrow?.disputes && hasOnlyTerminalDisputes(escrow.disputes);
    return (
      <div className={'flex items-center gap-2 lg:gap-4 flex-wrap'}>
        {GoBackButton}
        {ViewDisputeButton}
        {ChatButton}
        {showComplete && showCompleteButton && (
          <>
            <div className={'ml-auto'} />
            {CompleteTradeButton}
          </>
        )}
      </div>
    );
  }

  if (orderStatus === OrderStatus.TerminatedPostActivation) {
    return (
      <div className={'flex items-center gap-2 lg:gap-4 flex-wrap'}>
        {GoBackButton}
        {ChatButton}
      </div>
    );
  }

  if (orderStatus === OrderStatus.Traded) {
    if (isTabletAndLess) {
      return (
        <>
          <div className={'flex items-center gap-2 lg:gap-4'}>
            {GoBackButton}
            <div className={'ml-auto'} />
            {showCompleteButton && CompleteTradeButton}
          </div>

          <HorizontalDashedDelimiter className={'h-1 w-full my-6'} strokeLinecap={'round'} strokeDasharray={'0.1 5'} />

          <div className={'grid grid-cols-2 sm:grid-cols-3 gap-2 lg:gap-4 flex-wrap'}>
            {CancelTradeButton}
            {ReportAProblemButton}
            {OpenDisputeButton}
            {ChatButton}
          </div>
        </>
      );
    }
    return (
      <div className={'flex items-center gap-2 lg:gap-4 flex-wrap'}>
        {GoBackButton}
        {CancelTradeButton}
        {ReportAProblemButton}
        {OpenDisputeButton}
        {ChatButton}
        <div className={'ml-auto'} />
        {showCompleteButton && CompleteTradeButton}
      </div>
    );
  }

  if (orderStatus === OrderStatus.Completed) {
    return (
      <div className={'flex items-center gap-2 lg:gap-4 flex-wrap'}>
        {GoBackButton}
        {ChatButton}
        <div className={'ml-auto'} />
        {RateButton}
      </div>
    );
  }

  return (
    <div className={'flex items-center'}>
      {GoBackButton}
      <div className={'ml-auto'} />
    </div>
  );
}
